import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  ArrowLeft,
  Award,
  Calendar,
  User,
  FileText,
  MessageSquare,
  BookOpen,
  Target,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  Flag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Project } from "../api/gradesApi";

// Define the component's props interface
interface GradeDetailsPageProps {
  project: Project;
}

interface InstructorFeedbackProps {
  feedback: string[];
}

const InstructorFeedback: React.FC<InstructorFeedbackProps> = ({
  feedback,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(feedback.length - 1, prev + 1));
  };

  if (!feedback || feedback.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-100 rounded-lg p-4 border">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center text-base font-semibold ">
          <MessageSquare className="h-5 w-5 mr-3 text-bits-orange" />
          Instructor Feedback
        </div>
        {feedback.length > 1 && (
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="h-7 w-7 text-gray-500 hover:bg-gray-200"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              disabled={currentIndex === feedback.length - 1}
              className="h-7 w-7 text-gray-500 hover:bg-gray-200"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {feedback[currentIndex]}
      </p>
    </div>
  );
};

// Helper to get status badge styles
const getStatusBadge = (status: string | undefined) => {
  if (!status) {
    return <Badge variant="secondary">Not Started</Badge>;
  }

  const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, "");

  switch (normalizedStatus) {
    case "completed":
    case "graded":
      return <Badge className="bg-green-100 text-bits-green">Completed</Badge>;
    case "submitted":
      return <Badge className="bg-blue-100 text-bits-blue">Submitted</Badge>;
    case "overdue":
      return <Badge variant="destructive">Overdue</Badge>;
    case "notstarted":
      return <Badge variant="secondary">Not Started</Badge>;
    case "inprogress":
      return <Badge variant="secondary">In Progress</Badge>;
    default:
      console.warn("Unknown checkpoint status:", status);
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export default function GradeDetailsPage({ project }: GradeDetailsPageProps) {
  // Calculate overall score from the rubric
  const overallScore = project._overallScore || 0;
  const totalPossibleScore = project._maxScore || project.totalPoints || 0;

  // Function to convert score to letter grade
  const getLetterGrade = (score: number, total: number) => {
    const percentage = total > 0 ? (score / total) * 100 : 0;
    if (percentage >= 90) return "A";
    if (percentage >= 80) return "B";
    if (percentage >= 70) return "C";
    if (percentage >= 60) return "D";
    return "F";
  };

  const overallGrade = getLetterGrade(overallScore, totalPossibleScore);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Eye className="h-4 w-4 mr-2" />
          View Details
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto bg-white border custom-scroll">
        <DialogHeader>
          <DialogTitle className="flex justify-between items-start">
            <div>
              <h2 className="text-[16px] font-bold">{project.title}</h2>
              <p className="text-base text-muted-foreground">
                {project.subject}
              </p>
            </div>
            <Badge
              className={
                project.status === "graded"
                  ? "bg-green-100 text-bits-green mr-8"
                  : "bg-yellow-100 text-bits-warning mr-8"
              }
            >
              {project.projectStatus ||
                project.status.charAt(0).toUpperCase() +
                  project.status.slice(1)}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 pb-6 pt-2 border-b">
          <div>
            <p className="font-semibold">Instructor</p>
            <p className=" text-sm font-medium text-muted-foreground">
              {project.instructor}
            </p>
          </div>
          <div>
            <p className="font-semibold ">Timeline</p>
            <p className="text-sm font-medium text-muted-foreground">
              {new Date(project.dueDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
          <div>
            <p className="font-semibold">Overall Score</p>
            <p className="text-sm font-medium text-muted-foreground">{`${overallScore}/${totalPossibleScore}`}</p>
          </div>
          {project.projectStatus && (
            <div>
              <p className="font-semibold">Project Status</p>
              <p className="text-sm font-medium text-muted-foreground">
                {project.projectStatus}
              </p>
            </div>
          )}
        </div>

        {/* Overall Project Feedback Section */}
        {project.submissions?.[0]?.feedback && (
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mt-4">
            <div className="flex items-center text-base font-semibold mb-2">
              <MessageSquare className="h-5 w-5 mr-3 text-blue-600" />
              Overall Project Feedback
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {project.submissions[0].feedback}
            </p>
          </div>
        )}

        <div className="space-y-6 mt-4">
          <h3 className="flex items-center text-[14px] font-semibold">
            <Flag className="h-5 w-5 mr-3 text-purple-600" />
            Checkpoint Grades
          </h3>
          <div className="space-y-4">
            {project.checkpoints.map((checkpoint) => {
              return (
                <div key={checkpoint.id} className="p-4 border rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h4 className="font-semibold text-[14px]">
                        {checkpoint.title}
                      </h4>
                      {/* <p className="text-[14px] text-muted-foreground">
                        {project.subject} • {project.instructor}
                      </p> */}
                    </div>
                    <div className="flex gap-2">
                      {getStatusBadge(checkpoint.status)}
                    </div>
                  </div>

                  <div className="flex items-center text-lg font-bold mb-4 mt-2">
                    {checkpoint.grade && (
                      <p className="text-base text-gray-600 font-medium">
                        <span className="text-3xl text-gray-800 bg font-bold">
                          {checkpoint?.grade}
                        </span>{" "}
                        Grade
                      </p>
                    )}
                  </div>

                  {checkpoint.instructorFeedback &&
                    checkpoint.instructorFeedback.length > 0 && (
                      <InstructorFeedback
                        feedback={checkpoint.instructorFeedback}
                      />
                    )}
                  {project.projectType === "group" &&
                    project.isGroup &&
                    checkpoint?.totalScore !== undefined &&
                    checkpoint?.maxScore !== undefined &&
                    checkpoint.status === "graded" && (
                      <div className="text-sm my-1">
                        <span className="font-semibold">Score:</span>{" "}
                        {`${checkpoint.totalScore || 0}/${
                          checkpoint.maxScore || 0
                        }`}
                      </div>
                    )}
                  <div className="flex items-center text-sm text-muted-foreground justify-between">
                    <div className="flex items-center text-sm text-muted-foreground mt-4">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span>
                        {new Date(checkpoint.dueDate).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </div>
                    {project.projectType === "group" &&
                      project.isGroup &&
                      checkpoint.status === "graded" && (
                        <div className="mt-4">
                          <span className="font-semibold text-black">
                            Submitted by:
                          </span>{" "}
                          {checkpoint.submittedBy}
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
